import React, { useEffect, useState } from "react";
import styles from "./HotelHeightCalibrationComponent.module.css";
import MtiButton from "../../../Buttons/MtiButton";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import { ESuiteMode, EPsrState } from "../../../../Model/ModuleState";
import LayoutWithLog from "../LayoutWithLog";
import CalibrationApi from "../../../../api/CalibrationApi";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import { getModules } from "../../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../../Model/EModuleType";
import { ECommonProcessState } from "../../../../Model/ECommonProcessState";
import { ENotificationType } from "../../../../Model/ENotificationType";
import { sendNotification } from "../../../../redux/actions/NotificationAction";
import { useDispatch } from "react-redux";
import useCalibrationLog from "../../../Hooks/useCalibrationLog";
import { calibrationSignalRMap } from "../SettingsStorageRi1000";

interface Prop {
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  setIsCalibrationUpdated: (isUpdated: boolean) => void;
  setResetHandler: (onReset: {
    execute: () => void;
    disabled: boolean;
  }) => void;
  setRunningTabKey: React.Dispatch<React.SetStateAction<string>>;
}

export default function HotelHeightCalibrationComponent({
  setOnApply,
  setOnCancel,
  setIsCalibrationUpdated,
  setResetHandler,
  setRunningTabKey,
}: Prop) {
  const dispatch = useDispatch();
  const [processState, setProcessState] = useState(ECommonProcessState.Initial);
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );

  const onApply = () => {
    CalibrationApi.saveStorageHeightCalibration()
      .then(() => {
        dispatch(
          sendNotification(
            ENotificationType.Success,
            "The changes have been saved successfully"
          )
        );
        setCalibrationState(false);
      })
      .catch(() => {
        dispatch(
          sendNotification(
            ENotificationType.Error,
            "Saving changes encountered an error"
          )
        );
      });
  };

  const onCancel = () => {
    setCalibrationState(false);
    CalibrationApi.getStorageHeightCalibrationResult().then((result) => {
      setCurrentHeight(result.currentHeight);
      setInitialHeight(result.initialHeight);
    });
  };

  useEffect(() => {
    CalibrationApi.resetStorageHeightCalibration().then(() => {
      setCalibrationState(false);
    });
  }, []);

  const setCalibrationState = (isUpdated: boolean) => {
    setOnApply({
      execute: onApply,
      disabled: !isUpdated,
    });
    setOnCancel({
      execute: onCancel,
      disabled: !isUpdated,
    });
    setIsCalibrationUpdated(isUpdated);
  };

  const psrIdentifier = psrModules?.[0]?.identifier;

  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );
  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.Error;

  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrIdentifier).psr
  );

  // GET LOG LOGIC
  const [logs, setLogs] = useCalibrationLog(calibrationSignalRMap.hotelHeight);

  useEffect(() => {
    if (suiteMode == ESuiteMode.AutoCalibrating) {
      setProcessState(ECommonProcessState.Processing);
      setAllowRunCalibration(false);
      return;
    }

    if (suiteMode == ESuiteMode.Normal) {
      if (processState === ECommonProcessState.Processing) {
        if (psrState?.state === EPsrState.Fail) {
          setProcessState(ECommonProcessState.Fault);
          setRunningTabKey("");
        } else if (psrState?.state === EPsrState.Available) {
          setProcessState(ECommonProcessState.Finished);
          setRunningTabKey("");
        }
      }
      setAllowRunCalibration(true);
    }

    if (suiteMode == ESuiteMode.ArmCrashed || suiteMode == ESuiteMode.Error) {
      setProcessState(ECommonProcessState.Initial);
      return;
    }
  }, [suiteMode, suiteState]);

  useEffect(() => {
    switch (processState) {
      case ECommonProcessState.Finished:
        CalibrationApi.getStorageHeightCalibrationResult().then((result) => {
          setInitialHeight(result.initialHeight);
          setCurrentHeight(result.currentHeight);
          setProcessState(ECommonProcessState.Initial);
          setCalibrationState(true);
        });
        break;
      default:
        break;
    }
  }, [processState]);

  const [allowRunCalibration, setAllowRunCalibration] = useState(true);
  const [initialHeight, setInitialHeight] = useState(Number(0));
  const [currentHeight, setCurrentHeight] = useState(Number(0));

  useEffect(() => {
    CalibrationApi.getStorageHeightCalibrationResult().then((result) => {
      setInitialHeight(result.initialHeight);
      setCurrentHeight(result.currentHeight);
    });
  }, []);

  return (
    <div className={styles["outer-border"]}>
      <LayoutWithLog logMessages={logs ?? []}>
        <div className={styles["calibration-view"]}>
          <div className={styles["result-view"]}>
            <div className={styles["result-item"]}>
              <div style={{ width: "120px", height: "36px" }}>Initial</div>
              <div style={{ fontWeight: 500 }}>
                {Number(initialHeight).toFixed(2)}
              </div>
            </div>
            <div className={styles["result-item"]}>
              <div style={{ width: "120px", height: "36px" }}>Current</div>
              <div style={{ fontWeight: 500 }}>
                {Number(currentHeight).toFixed(2)}
              </div>
            </div>
          </div>
          <div className={styles["calibration-control"]}>
            <div className={styles["label-std"]} style={{ width: "232px" }}>
              Calibrating Height
            </div>
            <div className={styles["button-group"]}>
              <MtiButton
                variant="secondary"
                colorVariant="black"
                onClick={() => CalibrationApi.stopStorageHeightCalibration()}
                disabled={allowRunCalibration}
              >
                STOP
              </MtiButton>
              <MtiButton
                variant="primary"
                colorVariant="black"
                onClick={() => {
                  setRunningTabKey(calibrationSignalRMap.hotelHeight);
                  CalibrationApi.runStorageHeightCalibration();
                  setProcessState(ECommonProcessState.Started);
                }}
                disabled={!allowRunCalibration}
              >
                RUN
              </MtiButton>
            </div>
          </div>
        </div>
      </LayoutWithLog>
    </div>
  );
}
